import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { ReconciliationService } from "@/api";
import type { reconciliation_DebtorMonthlySeriesResponse } from "@/api";

// debtorMonthlySeriesKeys mirrors the `debtorAccountKeys.aggregates` shape:
// org-scoped (a Clerk org switch doesn't clear the cache, so without the org
// dimension a cached payload could surface another org's series during the
// staleTime window) and keyed by the sorted ID list so two pages with the
// same IDs share the cached payload while different pages don't collide.
export const debtorMonthlySeriesKeys = {
  all: ["reconciliation", "debtor-monthly-series"] as const,
  series: (orgId: string | null | undefined, ids: readonly string[]) =>
    [
      ...debtorMonthlySeriesKeys.all,
      orgId ?? null,
      [...ids].sort(),
    ] as const,
};

/**
 * useDebtorMonthlySeries fetches the per-debtor billed/collected monthly
 * series (Phase 6.3) for the visible page's IDs — one batched call that
 * powers each row's payment-timeliness heatmap + outstanding sparkline.
 *
 * Mirrors `useDebtorAccountAggregates`: the query is gated to a non-empty id
 * list (`enabled: ids.length > 0`) so an empty/loading list doesn't burn a
 * network call, and it shares the aggregates' 30s staleness window so the
 * money cells + the series stay coherent within a single page view.
 *
 * FRESHNESS POLICY (#1338) — see the FRESHNESS POLICY block at the top of
 * `useDebtorAccountsList.ts` for the reasoning; this records this hook's call.
 *
 * Focus refetch is ON, and it has to be, for the same reason the staleTime
 * matches. The heatmap and the money cell sit in the SAME table row and are
 * fed by two different queries. `useDebtorAccountAggregates` refetches on
 * focus; if this one did not, a tab-return would refresh the money cell and
 * leave the sparkline beside it describing an older world. Cost is the same
 * class as its twin — `GetDebtorMonthlySeriesByIDs` restricts through
 * `da.id = ANY(ids)` to the visible page and a trailing 12-month window, so it
 * is bounded by the page, not by the org's full history.
 */
export function useDebtorMonthlySeries(ids: readonly string[]) {
  const { orgId } = useAuth();
  return useQuery<reconciliation_DebtorMonthlySeriesResponse>({
    queryKey: debtorMonthlySeriesKeys.series(orgId, ids),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationDebtorAccountsMonthlySeries(
        ids.join(","),
      ),
    enabled: ids.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
}
